import React, { useMemo, useState } from 'react';
import type { BuffetMealPeriod, BuffetPackage } from '../../types';
import { BUFFET_MEAL_PERIODS, newBuffetPackageId } from '../../lib/buffetPackages';
import PlusIcon from '../icons/PlusIcon';
import EditIcon from '../icons/EditIcon';
import TrashIcon from '../icons/TrashIcon';

interface BuffetPackagesPanelProps {
  packages: BuffetPackage[];
  onSavePackages: (packages: BuffetPackage[]) => Promise<void> | void;
}

const emptyForm = (): Omit<BuffetPackage, 'id'> => ({
  name: '',
  mealPeriod: 'lunch',
  price: 0,
  description: '',
  isAvailable: true,
});

const BuffetPackagesPanel: React.FC<BuffetPackagesPanelProps> = ({ packages, onSavePackages }) => {
  const [editing, setEditing] = useState<BuffetPackage | null>(null);
  const [isNew, setIsNew] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [filterPeriod, setFilterPeriod] = useState<BuffetMealPeriod | 'all'>('all');

  const visible = useMemo(() => {
    const list = [...packages].sort((a, b) => {
      const order = { breakfast: 0, lunch: 1, dinner: 2 } as const;
      const d = order[a.mealPeriod] - order[b.mealPeriod];
      if (d !== 0) return d;
      return a.name.localeCompare(b.name);
    });
    if (filterPeriod === 'all') return list;
    return list.filter((p) => p.mealPeriod === filterPeriod);
  }, [packages, filterPeriod]);

  const openNew = () => {
    setIsNew(true);
    setEditing(null);
    setForm(emptyForm());
  };

  const openEdit = (pkg: BuffetPackage) => {
    setIsNew(false);
    setEditing(pkg);
    setForm({
      name: pkg.name,
      mealPeriod: pkg.mealPeriod,
      price: pkg.price,
      description: pkg.description || '',
      isAvailable: pkg.isAvailable !== false,
    });
  };

  const closeModal = () => {
    setEditing(null);
    setIsNew(false);
  };

  const handleSave = async () => {
    if (!form.name.trim() || form.price <= 0) {
      alert('Enter a name and a price greater than zero.');
      return;
    }
    setSaving(true);
    try {
      let next: BuffetPackage[];
      if (isNew) {
        next = [
          ...packages,
          {
            id: newBuffetPackageId(),
            name: form.name.trim(),
            mealPeriod: form.mealPeriod,
            price: form.price,
            description: form.description?.trim() || undefined,
            isAvailable: form.isAvailable !== false,
          },
        ];
      } else if (editing) {
        next = packages.map((p) =>
          p.id === editing.id
            ? {
                ...p,
                name: form.name.trim(),
                mealPeriod: form.mealPeriod,
                price: form.price,
                description: form.description?.trim() || undefined,
                isAvailable: form.isAvailable !== false,
              }
            : p,
        );
      } else {
        return;
      }
      await onSavePackages(next);
      closeModal();
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (pkg: BuffetPackage) => {
    if (!window.confirm(`Delete buffet package “${pkg.name}”?`)) return;
    await onSavePackages(packages.filter((p) => p.id !== pkg.id));
  };

  const showModal = isNew || editing != null;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Buffet packages</h2>
          <p className="text-sm text-gray-500">
            Staff pick these after New Order → Buffet. Not menu items — covers, timer, and kitchen rules stay the same.
            Add drinks and desserts as extras on the open buffet.
          </p>
        </div>
        <button
          type="button"
          onClick={openNew}
          className="inline-flex items-center gap-2 px-4 py-2 bg-pink-500 text-white text-sm font-semibold rounded-lg hover:bg-pink-600"
        >
          <PlusIcon className="w-4 h-4" />
          Add package
        </button>
      </div>

      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => setFilterPeriod('all')}
          className={`px-3 py-1 text-xs font-bold rounded-md ${filterPeriod === 'all' ? 'bg-pink-500 text-white' : 'bg-gray-100 text-gray-700'}`}
        >
          All
        </button>
        {BUFFET_MEAL_PERIODS.map((p) => (
          <button
            key={p.id}
            type="button"
            onClick={() => setFilterPeriod(p.id)}
            className={`px-3 py-1 text-xs font-bold rounded-md ${filterPeriod === p.id ? 'bg-pink-500 text-white' : 'bg-gray-100 text-gray-700'}`}
          >
            {p.label}
          </button>
        ))}
      </div>

      {visible.length === 0 ? (
        <p className="text-sm text-gray-500 py-8 text-center border border-dashed border-gray-200 rounded-lg">
          No buffet packages yet. Add breakfast, lunch, or dinner options for staff to choose on POS.
        </p>
      ) : (
        <ul className="divide-y divide-gray-200 bg-white border border-gray-200 rounded-lg overflow-hidden">
          {visible.map((pkg) => (
            <li key={pkg.id} className="flex items-center justify-between gap-3 px-4 py-3">
              <div className="min-w-0">
                <div className="font-medium text-gray-900 truncate">{pkg.name}</div>
                <div className="text-sm text-gray-500">
                  {pkg.mealPeriod.charAt(0).toUpperCase() + pkg.mealPeriod.slice(1)} · ${pkg.price.toFixed(2)} / person
                  {pkg.isAvailable === false ? ' · Hidden' : ''}
                </div>
                {pkg.description ? <div className="text-xs text-gray-400 mt-0.5 truncate">{pkg.description}</div> : null}
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button type="button" onClick={() => openEdit(pkg)} className="p-2 text-gray-500 hover:text-pink-600" title="Edit">
                  <EditIcon className="w-4 h-4" />
                </button>
                <button type="button" onClick={() => void handleDelete(pkg)} className="p-2 text-gray-500 hover:text-red-600" title="Delete">
                  <TrashIcon className="w-4 h-4" />
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {showModal && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
            <h3 className="text-xl font-bold text-gray-800 mb-4">{isNew ? 'Add buffet package' : 'Edit buffet package'}</h3>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  placeholder="e.g. Adult Lunch Buffet"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Meal period</label>
                <select
                  value={form.mealPeriod}
                  onChange={(e) => setForm((f) => ({ ...f, mealPeriod: e.target.value as BuffetMealPeriod }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                >
                  {BUFFET_MEAL_PERIODS.map((p) => (
                    <option key={p.id} value={p.id}>{p.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Price per person ($)</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.price || ''}
                  onChange={(e) => setForm((f) => ({ ...f, price: parseFloat(e.target.value) || 0 }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description (optional)</label>
                <textarea
                  value={form.description || ''}
                  onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                />
              </div>
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={form.isAvailable !== false}
                  onChange={(e) => setForm((f) => ({ ...f, isAvailable: e.target.checked }))}
                  className="h-4 w-4 text-pink-600 rounded"
                />
                Available for staff on POS
              </label>
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <button type="button" onClick={closeModal} className="px-4 py-2 bg-gray-100 rounded-lg text-sm font-semibold">
                Cancel
              </button>
              <button
                type="button"
                disabled={saving}
                onClick={() => void handleSave()}
                className="px-4 py-2 bg-pink-500 text-white rounded-lg text-sm font-semibold hover:bg-pink-600 disabled:opacity-60"
              >
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BuffetPackagesPanel;
